import { useState, useEffect, useCallback } from 'react'
import { apiGet, ApiError } from '../lib/api'

export interface User {
  id: string
  email: string
  displayName: string
  role: 'admin' | 'professional' | 'receptionist'
  consultationRoom?: string
}

export interface Office {
  id: string
  name: string
  queueOrder: 'arrival' | 'appointment'
  isPaused: boolean
  isClosed: boolean
}

export interface OfficeRef {
  id: string
  name: string
}

interface AuthResponse {
  user: User
  office: Office | null
  offices: OfficeRef[]
}

interface UseAuthReturn {
  user: User | null
  office: Office | null
  offices: OfficeRef[]
  loading: boolean
  logout: () => void
  refresh: () => Promise<void>
  switchOffice: (officeId: string) => Promise<void>
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null)
  const [office, setOffice] = useState<Office | null>(null)
  const [offices, setOffices] = useState<OfficeRef[]>([])
  const [loading, setLoading] = useState(true)

  const fetchMe = useCallback(async () => {
    try {
      const data = await apiGet<AuthResponse>('/api/auth/me')
      setUser(data.user)
      setOffice(data.office)
      setOffices(data.offices ?? [])
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null)
        setOffice(null)
        setOffices([])
      } else {
        setUser(null)
        setOffice(null)
        setOffices([])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchMe()
  }, [fetchMe])

  const logout = useCallback(() => {
    void fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).finally(() => {
      setUser(null)
      setOffice(null)
      setOffices([])
      window.location.href = '/login'
    })
  }, [])

  const switchOffice = useCallback(async (officeId: string) => {
    await fetch('/api/auth/switch-office', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ officeId }),
    })
    // Full reload to reset all office-specific state (queue, events, etc.)
    window.location.reload()
  }, [])

  return { user, office, offices, loading, logout, refresh: fetchMe, switchOffice }
}
